import {
  fetchCategories,
  fetchCategoryPicMaps,
  fetchStatuses,
  fetchUsers,
} from '../../../api/ismApi';

const MAX_ATTEMPTS = 4;
const RETRY_DELAYS_MS = [500, 1000, 1500];

let cachedMasterData = null;
let inFlightMasterData = null;
let masterDataError = null;
let generation = 0;

function isAuthError(error) {
  return error?.status === 401 || error?.status === 403;
}

function wait(delay) {
  return new Promise(resolve => setTimeout(resolve, delay));
}

async function fetchMasterDataAttempt() {
  const resources = [
    ['categories', fetchCategories],
    ['statuses', fetchStatuses],
    ['users', fetchUsers],
    ['categoryPicMaps', fetchCategoryPicMaps],
  ];
  const results = await Promise.all(resources.map(async ([name, fetcher]) => {
    const result = await fetcher();
    if (!Array.isArray(result?.items)) {
      throw new Error(`Invalid ${name} master-data response.`);
    }
    return [name, result.items];
  }));

  return Object.fromEntries(results);
}

async function fetchWithRetry(requestGeneration) {
  let lastError;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    try {
      return await fetchMasterDataAttempt();
    } catch (error) {
      lastError = error;
      // Auth failures are not transient; retrying only produces a 401/403 loop.
      if (isAuthError(error) || requestGeneration !== generation) throw error;
      if (attempt < MAX_ATTEMPTS - 1) await wait(RETRY_DELAYS_MS[attempt]);
      if (requestGeneration !== generation) throw error;
    }
  }
  throw lastError;
}

export function loadMasterData({ force = false } = {}) {
  if (!force && cachedMasterData) return Promise.resolve(cachedMasterData);
  if (inFlightMasterData) return inFlightMasterData;

  const requestGeneration = generation;
  const request = fetchWithRetry(requestGeneration);
  inFlightMasterData = request;
  request.then(data => {
    if (requestGeneration !== generation) return;
    cachedMasterData = data;
    masterDataError = null;
  }).catch(error => {
    if (requestGeneration !== generation) return;
    masterDataError = error;
  }).finally(() => {
    if (inFlightMasterData === request) inFlightMasterData = null;
  });
  return request;
}

export function getMasterDataError() {
  return masterDataError;
}

export function invalidateMasterData() {
  cachedMasterData = null;
  masterDataError = null;
}

// Called on login, logout and user change: drops the cache, the error and any
// in-flight request so nothing from a previous session is reused or cached.
export function resetMasterData() {
  generation += 1;
  cachedMasterData = null;
  inFlightMasterData = null;
  masterDataError = null;
}

